import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useCreateApiKey } from '@/api/api-keys';

interface CreateApiKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the full secret once created — shown once to the user. */
  onCreated: (key: string, name: string) => void;
}

export function CreateApiKeyDialog({ open, onOpenChange, onCreated }: CreateApiKeyDialogProps) {
  const { show } = useToast();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setError(null);
    }
  }, [open]);

  const create = useCreateApiKey({
    onSuccess: (key) => {
      onCreated(key, name.trim());
      onOpenChange(false);
    },
    onError: () => show({ tone: 'danger', title: 'Could not create API key' }),
  });

  function handleSubmit() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Key name is required.');
      return;
    }
    setError(null);
    create.mutate(trimmed);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Create API key</DialogTitle>
          <DialogDescription>
            Give the key a name so you can recognize it later.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form
            id="create-api-key-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <Field label="Key name" required error={error ?? undefined}>
              {(props) => (
                <Input
                  {...props}
                  autoFocus
                  placeholder="Production, CI, …"
                  value={name}
                  invalid={!!error}
                  onChange={(e) => setName(e.target.value)}
                />
              )}
            </Field>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-api-key-form"
            disabled={!name.trim() || create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create key'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
